import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import type { BadgeSelection } from './badge-display-policy';
import { LOCALE_COMMON_PATHS, LOCALE_LISTING_PATHS, Namespaces } from 'types/locales';
import { formatI18nDate } from 'utils/i18n-format-date';

export type BadgeType = 'new' | 'price-reduced' | 'open-house' | 'virtual-tour' | 'coming-soon' | 'exp-early-access';

export interface BadgeData {
  type: BadgeType;
  label: string;
  iconColor?: string;
}

export interface UseBadgesOptions {
  selections: BadgeSelection[];
  primaryColor?: string;
}

export interface UseBadgesResult {
  badges: BadgeData[];
  hasBadges: boolean;
}

export function useBadges({ selections, primaryColor }: UseBadgesOptions): UseBadgesResult {
  const { t, i18n } = useTranslation(Namespaces.LISTING);
  const locale = i18n?.language || 'en';

  const badges = useMemo(() => {
    return selections.map((selection): BadgeData | null => {
      if (selection.comingSoon) {
        // Lives in COMMON (not LISTING): this badge also renders on listing/map
        // cards, whose pages don't reliably load the LISTING namespace, and
        // COMMON is always loaded. With a known go-live date we show it
        // ("Coming Soon May 16th"); otherwise we fall back to a dateless label.
        return {
          type: 'coming-soon',
          label: selection.comingSoonDate
            ? t(LOCALE_COMMON_PATHS.LISTING_BADGE_COMING_SOON_DATED, {
              date: formatI18nDate(selection.comingSoonDate, 'monthDay', locale),
            })
            : t(LOCALE_COMMON_PATHS.LISTING_BADGE_COMING_SOON),
        };
      }

      if (selection.expEarlyAccess) {
        return {
          type: 'exp-early-access',
          label: t(LOCALE_COMMON_PATHS.LISTING_BADGE_EXP_EARLY_ACCESS),
        };
      }

      if (selection.addedAt) {
        return {
          type: 'new',
          label: t(LOCALE_LISTING_PATHS.BADGE_NEW),
        };
      }

      if (selection.priceHistory) {
        return {
          type: 'price-reduced',
          label: t(LOCALE_LISTING_PATHS.BADGE_PRICE_REDUCED),
          iconColor: primaryColor,
        };
      }

      if (selection.openHouse) {
        return {
          type: 'open-house',
          label: t(LOCALE_LISTING_PATHS.BADGE_OPEN_HOUSE),
        };
      }

      if (selection.virtualTourUrl) {
        return {
          type: 'virtual-tour',
          label: t(LOCALE_LISTING_PATHS.BADGE_VIRTUAL_TOUR),
        };
      }

      return null;
    }).filter((badge): badge is BadgeData => badge !== null && badge.label !== '');
  }, [selections, primaryColor, t, locale]);

  return {
    badges,
    hasBadges: badges.length > 0,
  };
}
